import * as actions from './actions';
import { createReducer } from '@reduxjs/toolkit';
import moment from 'moment';
import sortBy from 'lodash.sortby';
import { produce } from 'immer';
import { ProductType } from 'constants.js';

const deCamelizer = str => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9]+)/g, ' $1')
    .replace(/^./, str => str.toUpperCase());
};

const initialState = {
  getting: true,
  gettingPaginated: true,
  gettingAllCampaigns: false,
  creating: false,
  creatingPaginated: false,
  updating: false,
  deleting: false,
  deletingPaginated: false,
  list: [],
  paginatedList: [],
  advancedPaginatedList: [],
  allCampaignsList: [],
  current: {
    name: '',
    type: 'EMAIL',
    emailCampaignData: {
      emailSubject: '',
      fromAddress: '',
      fromName: '',
      replyAddress: ''
    },
    attachmentTemplateUuids: [],
    backport: false,
    promotional: false,
    startDate: null,
    endDate: null,
    productType: ProductType.Product
  },
  error: {
    message: null,
    raw: null
  },
  isCampaignSaved: false,
  isCampaignSaving: false,
  isScheduleNowProcessing: false,
  filters: {
    searchInput: '',
    displayActive: true,
    sortBy: 'name',
    sortOrder: 'ASC',
    rowsPerPage: 10,
    pageNo: 0,
    totalCount: 0,
    filterTypes: [],
    filterTemplates: [],
    filterProductNames: [],
    filterProductCategories: [],
    filterOccurrenceTypes: [],
    filterMarketingTypes: []
  }
};

const types = {
  EMAIL: 'Email',
  SMS: 'SMS',
  ON_EVENT: 'Automatic',
  ON_DEMAND: 'Manual',
  SCHEDULED: 'On Date'
};

export default createReducer(initialState, {
  [actions.GET_CAMPAIGNS]: state => {
    state.error = {};
    state.getting = true;
    return state;
  },
  [actions.GET_CAMPAIGN]: state => {
    state.error = {};
    return state;
  },
  [actions.CREATE_CAMPAIGN]: state => {
    state.isScheduleNowProcessing = false;
    state.error = {};
    state.isCampaignSaved = false;
    state.isCampaignSaving = true;
    return state;
  },
  [actions.UPDATE_CAMPAIGN]: state => {
    state.isScheduleNowProcessing = false;
    state.current = {
      ...state.current,
      type: state.current.type || 'EMAIL',
      active: state.current.active === undefined ? true : state.current.active,
      campaignOccurrenceType:
        state.current.campaignOccurrenceType || 'SCHEDULED'
    };
    state.isCampaignSaved = false;
    state.isCampaignSaving = true;
    return state;
  },
  [actions.DELETE_CAMPAIGNS]: state => {
    return state;
  },
  [actions.GETTING_CAMPAIGNS]: state => {
    state.getting = true;
    state.error = {};
    return state;
  },
  [actions.GETTING_CAMPAIGN]: state => {
    state.getting = true;
    state.error = {
      message: null,
      raw: null
    };
  },
  [actions.CLEAR_CAMPAIGN_STORE]: state => {
    let updatedState = {
      ...initialState
    };
    return updatedState;
  },
  [actions.CREATING_CAMPAIGN]: state => {
    state.creating = true;
    state.error = {};
    return state;
  },
  [actions.UPDATING_CAMPAIGN]: state => {
    state.updating = true;
    state.error = {};
    return state;
  },
  [actions.DELETING_CAMPAIGNS]: state => {
    state.deleting = true;
    return state;
  },
  [actions.GETTING_CAMPAIGN_SUCCEEDED]: (state, action) => {
    const { campaign, eventMetaData = [] } = action.payload;

    let hydratedEventMetaData = sortBy(
      eventMetaData.map(data => ({
        ...data,
        productName: data.schemaType === 'EVENT'
          ? `Event Product - ${campaign.productName}`
          : data.schemaType === 'ENRICHMENT' ? `Enrichment Product - ${data.enrichmentProduct}` : data.schemaTypeDisplayName,
        schemaConditionAttributes: data.schemaConditionAttributes
          .map(a => ({
            key: a.conditionAttribute,
            name:
              !a.displayName || a.displayName === ''
                ? deCamelizer(a.conditionAttribute)
                : a.displayName,
            dataType: a.conditionAttributeType || a.attributeFieldType,
            text: `{{${a.conditionAttribute}}}`,
            attributeFieldType: a.attributeFieldType,
            metaAttribute: a.metaAttribute,
            description: a.description,
            productName: data.schemaType === 'EVENT'
              ? `Event Product - ${campaign.productName}`
              : data.schemaType === 'ENRICHMENT' ? `Enrichment Product - ${data.enrichmentProduct}` : data.schemaTypeDisplayName,
            subAttribute: a.subAttribute
          }))
      })).filter(data => campaign.campaignOccurrenceType === 'INTERNAL_SURVEY_RESPONSE' || campaign.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE' || campaign.productType === 'LIST' ? (data.schemaType === 'EVENT' || data.schemaType === 'ENRICHMENT' || data.schemaType === 'LIST') : data ),
      'name'
    );

    let updatedCampaign = produce(campaign, campaignDraft => {
      campaignDraft.eventMetaData = hydratedEventMetaData;
    });

    state.getting = false;
    state.current = { ...updatedCampaign };
    return state;
  },
  [actions.INITIATE_SAVE_AND_SCHEDULE_NOW]: state => {
    state.isScheduleNowProcessing = true;
    return state;
  },
  [actions.RESET_SCHEDULE_NOW]: state => {
    state.isScheduleNowProcessing = false;
    return state;
  },
  [actions.CREATING_CAMPAIGN_SUCCEEDED]: (state, action) => {
    const { campaign } = action.payload;
    let updatedCampaign = {
      ...campaign,
      displayType: types[campaign.type],
      displayOccurrenceType: types[campaign.campaignOccurrenceType],
      displayDate:
        types[campaign.campaignOccurrenceType] === types.SCHEDULED
          ? moment(new Date(campaign.startDate)).format('MMMM DD YYYY')
          : undefined
    };

    state.list.push(updatedCampaign);
    state.isCampaignSaved = true;
    state.isCampaignSaving = false;
    state.current = {
      ...state.current,
      uuid: updatedCampaign.uuid,
      businessContextId: updatedCampaign.businessContextId,
      _hasSetCampaignExclusionRules: true
    };
    return state;
  },
  [actions.UPDATING_CAMPAIGN_SUCCEEDED]: (state, action) => {
    state.current = { ...action.payload };
    state.getting = false;
    state.create = false;
    state.updating = false;
    state.list = state.list.map(campaign => {
      if (campaign.uuid === action.payload.uuid) {
        return action.payload;
      }
      return campaign;
    });
    state.isCampaignSaved = true;
    state.isCampaignSaving = false;
    return state;
  },
  [actions.DELETING_CAMPAIGNS_SUCCEEDED]: (state, action) => {
    state.deleting = false;
    state.current = action.payload.find(uuid => uuid === state.current?.uuid);
    state.paginatedList = state.paginatedList.map(campaign => {
      if (campaign.uuid === action.payload[0]) {
        campaign.active = !campaign.active;
      }
      return campaign;
    });

    return state;
  },
  [actions.GETTING_CAMPAIGNS_SUCCEEDED]: (state, action) => {
    if (JSON.stringify(state.list) !== JSON.stringify(action.payload)) {
      state.list = action.payload.map(campaign => ({
        ...campaign,
        displayType: types[campaign.type],
        displayOccurrenceType: types[campaign.campaignOccurrenceType],
        displayDate:
          types[campaign.campaignOccurrenceType] === types.SCHEDULED
            ? moment(new Date(campaign.startDate)).format('MMMM DD YYYY')
            : undefined
      }));
    }
    state.getting = false;

    return state;
  },
  [actions.GETTING_CAMPAIGNS_FAILED]: (state, action) => {
    state.error = {
      raw: action.payload
    };
    return state;
  },
  [actions.GETTING_CAMPAIGN_FAILED]: (state, action) => {
    state.error = {
      raw: action.payload
    };
    return state;
  },
  [actions.CREATING_CAMPAIGN_FAILED]: (state, action) => {
    state.error = {
      raw: action.payload
    };
    state.isCampaignSaved = false;
    state.isCampaignSaving = false;
    return state;
  },
  [actions.UPDATING_CAMPAIGN_FAILED]: (state, action) => {
    state.error = {
      raw: action.payload
    };
    state.isCampaignSaved = false;
    state.isCampaignSaving = false;
    return state;
  },
  [actions.DELETING_CAMPAIGNS_FAILED]: (state, action) => {
    state.getting = false;
    state.creating = false;
    state.updating = false;
    state.deleting = false;
    state.error = {
      message: action.payload.message,
      raw: action.payload
    };
    return state;
  },
  [actions.SET_CURRENT_CAMPAIGN]: (state, action) => {
    state.isCampaignSaved = false;
    state.current = action.payload;
    return state;
  },
  [actions.SET_COPY_CURRENT_CAMPAIGN]: (state, action) => {
    state.current = action.payload;
    return state;
  },
  [actions.UNSET_CURRENT_CAMPAIGN]: state => {
    state.current = { ...initialState.current };
    return state;
  },
  [actions.CLEAR_CAMPAIGN_ERROR]: state => {
    state.error = { ...initialState.error };
    return state;
  },
  [actions.GET_PAGINATED_CAMPAIGNS]: state => {
    return state;
  },
  [actions.GETTING_PAGINATED_CAMPAIGNS]: state => {
    state.gettingPaginated = true;
    return state;
  },
  [actions.GETTING_PAGINATED_CAMPAIGNS_SUCCEEDED]: (state, action) => {
    state.gettingPaginated = false;
    state.paginatedList = action.payload.data.map(campaign => ({
      ...campaign,
      displayType: types[campaign.type],
      displayOccurrenceType: types[campaign.campaignOccurrenceType],
      displayDate:
        types[campaign.campaignOccurrenceType] === types.SCHEDULED
          ? moment(new Date(campaign.startDate)).format('MMMM DD YYYY')
          : undefined,
      sendDate:
        types[campaign.campaignOccurrenceType] === 'Automatic' ||
        types[campaign.campaignOccurrenceType] === 'Manual'
          ? types[campaign.campaignOccurrenceType]
          : types[campaign.campaignOccurrenceType] === types.SCHEDULED
          ? moment(new Date(campaign.startDate)).format('MMMM DD YYYY')
          : undefined
    }));
    return state;
  },
  [actions.GETTING_PAGINATED_CAMPAIGNS_FAILED]: (state, action) => {
    state.gettingPaginated = false;
    state.creatingPaginated = false;
    state.deletingPaginated = false;
    state.error = {
      message: action.payload.message,
      raw: action.payload
    };
    return state;
  },
  [actions.GETTING_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS]: state => {
    state.gettingPaginated = true;
    return state;
  },
   [actions.GETTING_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS_SUCCEEDED]: (state, action) => {
    state.gettingPaginated = false;
    // Handle both array (from selector) and object with data property (from API)
    const campaigns = Array.isArray(action.payload) ? action.payload : action.payload.data;
    const newList = (campaigns || []).map(campaign => ({
      ...campaign
    }));
    // Only update if the list has actually changed to prevent infinite loops
    const currentUuids = state.advancedPaginatedList.map(c => c.uuid).join(',');
    const newUuids = newList.map(c => c.uuid).join(',');
    if (currentUuids !== newUuids) {
      state.advancedPaginatedList = newList;
    }
    return state;
  },
  [actions.GETTING_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS_FAILED]: (state, action) => {   
    state.gettingPaginated = false;
    state.error = {
      message: action.payload.message,
      raw: action.payload
    };
    return state;
  },
  [actions.GET_ALL_ACTIVE_CAMPAIGNS]: state => {
    state.gettingAllCampaigns = true;
    state.allCampaignsList = [];
    return state;
  },
  [actions.GETTING_ALL_ACTIVE_CAMPAIGNS_SUCCEEDED]: (state, action) => {
    state.gettingAllCampaigns = false;
    state.allCampaignsList = action.payload;
    return state;
  },
  [actions.GETTING_ALL_ACTIVE_CAMPAIGNS_FAILED]: (state, action) => {
    state.gettingAllCampaigns = false;
    state.allCampaignsList = [];
    state.error = {
      message: action.payload.message,
      raw: action.payload
    };
    return state;
  },
  [actions.UPDATE_CAMPAIGN_FILTERS]: (state, action) => {
    state.gettingPaginated = true;
    state.filters = {
      ...state.filters,
      ...action.payload
    }
    return state;
  },
  [actions.SET_CAMPAIGN_FILTERS]: (state, action) => {
    state.filters = {
      ...state.filters,
      ...action.payload
    }
    return state;
  },
  [actions.RESET_CAMPAIGN_FILTERS]: state => {
    state.gettingPaginated = true;
    state.filters = { ...initialState.filters }
    return state;
  },
});
